/**
 * `Leave pool`, alone under a divider. An owner sees no button at all: the
 * leave endpoint refuses an owner with a conflict, because ownership moves
 * through a transfer flow, not this screen. The generated leave hook already
 * invalidates the pools-list query on success; this component adds no
 * hand-written cache update.
 */
import { usePoolLeave } from "@sportsmet/pool-sdk/private";
import { Button, confirmDestructive } from "@sportsmet/ui";
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  LEAVE_POOL_CONFIRM_BODY,
  LEAVE_POOL_CONFIRM_TITLE,
  LEAVE_POOL_LABEL,
} from "./poolHomeState";

export type typeLeavePoolButtonProps = {
  poolId: string;
  canLeave: boolean;
};

export const LeavePoolButton = ({
  poolId,
  canLeave,
}: typeLeavePoolButtonProps): ReactNode => {
  const leave = usePoolLeave();
  const navigate = useNavigate();

  if (!canLeave) {
    return null;
  }

  async function handleLeave(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: LEAVE_POOL_CONFIRM_TITLE,
      body: LEAVE_POOL_CONFIRM_BODY,
      confirmLabel: LEAVE_POOL_LABEL,
    });
    if (!isConfirmed) return;

    try {
      const result = await leave.mutateAsync({ poolId });
      if (showApiError(result)) return;
      void navigate({
        to: "/pools",
        search: {
          poolId: undefined,
          q: undefined,
          sheet: undefined,
          dialog: undefined,
        },
      });
    } catch {
      showOfflineError();
    }
  }

  return (
    <>
      <div className="border-t border-hairline pt-4" />
      <Button
        variant="destructive"
        data-testid="pool-home-leave"
        disabled={leave.isPending}
        onClick={() => {
          void handleLeave();
        }}
      >
        {LEAVE_POOL_LABEL}
      </Button>
    </>
  );
};
